import React, { useState, useMemo } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { RadioGroup, FormControlLabel, Radio } from "@mui/material";
import MKAlert from "components/MKAlert";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKButton from "components/MKButton";
import MKInput from "components/MKInput";
import MKTypography from "components/MKTypography";

// Material Kit 2 PRO React examples
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

// Routes
import routes from "routes";
import footerRoutes from "footer.routes";

// Images
import bgImage from "assets/images/examples/content-5.jpg";

//icons
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EmailIcon from "@mui/icons-material/Email";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

//motion
import { motion } from "framer-motion";

function Employee() {
  const SERVER_URL = process.env.REACT_APP_SERVER_URL;

  const [form, setForm] = useState({
    fullName: "",
    phone: "",
    email: "",
    originResidence: "",
    ageReligion: "",
    workExperience: "",
    cookingSkills: "",
    certificates: "",
    education: "",
    contractCommitment: "",
    previousCompanies: "",
    livePreference: "",
    familyExperience: "",
    languageLevels: "",
    idStatus: "",
    websiteRegistration: "",
    contacts: "",
    address: "",
    sex: "",
    howHeard: "",
    howHeardOther: "",
  });

  const [idFile, setIdFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("info");
  const [_status, setStatus] = useState("idle");
  const [openModal, setOpenModal] = useState(false);
  const navigate = useNavigate();

  const handleCloseModal = (event, reason) => {
    if (reason === "clickaway") return;
    setOpenModal(false);
  };

  const handleChange = (field) => (e) => {
    const value = e.target.value;

    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "howHeard" &&
        value !== "Other" && {
          howHeardOther: "",
        }),
    }));
  };

  // FIX #1: howHeardOther is only required when howHeard === "Other"
  const isFormValid = useMemo(() => {
    const requiredFields = Object.entries(form).filter(([key]) => {
      if (key === "howHeardOther") return form.howHeard === "Other";
      return true;
    });

    const allFieldsFilled = requiredFields.every(([, value]) => value && value.trim() !== "");

    return allFieldsFilled && idFile;
  }, [form, idFile]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid) return;

    setIsLoading(true);
    setAlertMsg("");

    try {
      const formData = new FormData();

      Object.entries(form).forEach(([key, value]) => {
        formData.append(key, value);
      });

      formData.append("id_file", idFile);

      const res = await axios.post(`${SERVER_URL}/career/add/`, formData);

      // FIX #3: backend now returns JSON { status: "success" }
      if (res.status === 200 && res.data?.status === "success") {
        setAlertMsg("Thank you... we'll get back to you soon");
        setAlertSeverity("success");
        setStatus("success");
        setIsSubmitted(true);
        setOpenModal(true);

        setTimeout(() => {
          navigate("/");
        }, 3000);
      } else {
        setStatus("error");
        // FIX #5: show whatever message the backend returned
        setAlertMsg(res.data?.message || "Submission failed. Please try again.");
        setAlertSeverity("error");
      }
    } catch (err) {
      console.error("Submission error:", err);
      // FIX #5: clearly surface backend error messages
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data ||
        "An error occurred. Please try again later.";
      setAlertMsg(typeof errorMessage === "string" ? errorMessage : "An unexpected error occurred.");
      setAlertSeverity("error");
      setStatus("error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <>
        <DefaultNavbar
          routes={routes}
          action={{
            type: "internal",
            route: "/booking",
            label: "BOOK NOW",
            color: "info",
          }}
          transparent
          light
        />
        <MKBox
          minHeight="50vh"
          width="100%"
          sx={{
            backgroundImage: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
              `${linearGradient(
                rgba(gradients.dark.main, 0.6),
                rgba(gradients.dark.state, 0.6)
              )}, url(${bgImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            display: "grid",
            placeItems: "center",
          }}
        >
          <Container>
            <Grid
              container
              item
              xs={12}
              lg={8}
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              sx={{ mx: "auto", textAlign: "center" }}
            >
              <MKTypography
                variant="h2"
                color="white"
                mb={2}
                sx={({ breakpoints, typography: { size } }) => ({
                  [breakpoints.down("md")]: {
                    fontSize: size["3xl"],
                  },
                })}
              >
                Build Your Future With Us
              </MKTypography>
            </Grid>
          </Container>
        </MKBox>
        <Card
          sx={{
            p: { xs: 2, md: 4 },
            mx: { xs: 1, md: 4 },
            mt: -8,
            mb: 4,
            borderRadius: "2rem",
            backgroundColor: "rgba(255,255,255,0.85)",
            backdropFilter: "blur(25px) saturate(200%)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
          }}
        >
          <MKBox component="section" py={{ xs: 4, lg: 6 }}>
            <Container>
              <MKBox component="section" py={{ xs: 4, lg: 6 }}>
                <Container>
                  <Card
                    sx={{
                      p: { xs: 2, md: 4 },
                      mx: { xs: 1, md: 4 },
                      mt: -8,
                      borderRadius: "2rem",
                      backgroundColor: "rgba(255,255,255,0.85)",
                      backdropFilter: "blur(25px) saturate(200%)",
                      boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                    }}
                  >
                    {alertMsg.length > 0 && (
                      <motion.div
                        initial={{ x: -200, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ duration: 1.5, ease: "easeOut" }}
                      >
                        {/* FIX #5: use alertSeverity so errors show in red, success in blue */}
                        <MKAlert color={alertSeverity === "error" ? "error" : "info"}>
                          {alertMsg}
                        </MKAlert>
                      </motion.div>
                    )}

                    <MKBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <MKTypography variant="h3" fontWeight="medium">
                        Job Application
                      </MKTypography>
                    </MKBox>

                    <MKTypography variant="body2" mb={4} color="text.secondary">
                      Complete all questions thoughtfully to be considered for the shortlist and
                      initial interview with <strong>BEST HELPER EVERYWHERE</strong>.<br />
                      <strong>Tip:</strong> Avoid one-word answers like "yes" or "no". Provide
                      clear, detailed responses to showcase your experience and skills. This is your
                      first step in the application process.
                    </MKTypography>

                    <form onSubmit={handleSubmit}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                          <MKTypography variant="body2" fontWeight="medium" mb={1}>
                            What is your full name?
                          </MKTypography>
                          <MKInput
                            label=""
                            fullWidth
                            required
                            value={form.fullName}
                            onChange={handleChange("fullName")}
                          />
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <MKTypography variant="body2" fontWeight="medium" mb={1}>
                            Email
                          </MKTypography>
                          <MKInput
                            label=""
                            fullWidth
                            required
                            placeholder="your-email@mail.com"
                            onChange={handleChange("email")}
                            value={form.email}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <MKTypography variant="body2" fontWeight="medium" mb={1}>
                            Phone Number{" "}
                          </MKTypography>
                          <MKInput
                            label=""
                            fullWidth
                            required
                            placeholder="+250 7xx xxx xxx"
                            onChange={handleChange("phone")}
                            value={form.phone}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <MKTypography variant="body2" fontWeight="medium" mb={1}>
                            Where are you from and where do you live now?{" "}
                          </MKTypography>
                          <MKInput
                            label=""
                            multiline
                            required
                            rows={3}
                            fullWidth
                            value={form.originResidence}
                            onChange={handleChange("originResidence")}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <MKTypography variant="body2" fontWeight="medium" mb={1}>
                            How old are you and what is your religion?{" "}
                          </MKTypography>
                          <MKInput
                            label=""
                            multiline
                            required
                            rows={2}
                            fullWidth
                            value={form.ageReligion}
                            onChange={handleChange("ageReligion")}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <MKTypography variant="body2" fontWeight="medium" mb={1}>
                            Have you worked as a nanny, housemaid, chef, or housekeeper before?
                            Describe your experience and number of years.
                          </MKTypography>
                          <MKInput
                            label=""
                            multiline
                            rows={4}
                            fullWidth
                            required
                            value={form.workExperience}
                            onChange={handleChange("workExperience")}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <MKTypography variant="body2" fontWeight="medium" mb={1}>
                            How many types of international food can you cook? Please describe.{" "}
                          </MKTypography>
                          <MKInput
                            label=""
                            multiline
                            rows={3}
                            fullWidth
                            required
                            value={form.cookingSkills}
                            onChange={handleChange("cookingSkills")}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <MKTypography variant="body2" fontWeight="medium" mb={1}>
                            Do you have any certificate, diploma, or related qualification?{" "}
                          </MKTypography>
                          <MKInput
                            label=""
                            multiline
                            rows={3}
                            fullWidth
                            required
                            value={form.certificates}
                            onChange={handleChange("certificates")}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <MKTypography variant="body2" fontWeight="medium" mb={1}>
                            Have you completed secondary school or university? Please explain.{" "}
                          </MKTypography>
                          <MKInput
                            label=""
                            multiline
                            rows={3}
                            fullWidth
                            required
                            value={form.education}
                            onChange={handleChange("education")}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <MKTypography variant="body2" fontWeight="medium" mb={1}>
                            If you get a job, are you willing to complete a 1-year contract? Explain
                            why.{" "}
                          </MKTypography>
                          <MKInput
                            label=""
                            multiline
                            rows={3}
                            fullWidth
                            required
                            value={form.contractCommitment}
                            onChange={handleChange("contractCommitment")}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <MKTypography variant="body2" fontWeight="medium" mb={1}>
                            Have you ever worked with or registered at another company before?
                            Describe.{" "}
                          </MKTypography>
                          <MKInput
                            label=""
                            multiline
                            rows={3}
                            fullWidth
                            required
                            value={form.previousCompanies}
                            onChange={handleChange("previousCompanies")}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <MKTypography variant="body2" fontWeight="medium" mb={1}>
                            Can you work live-in or live-out? Please explain your preference.
                          </MKTypography>
                          <MKInput
                            label=""
                            multiline
                            rows={2}
                            fullWidth
                            required
                            value={form.livePreference}
                            onChange={handleChange("livePreference")}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <MKTypography variant="body2" fontWeight="medium" mb={1}>
                            Have you worked with expert or white families before? Describe your
                            experience.{" "}
                          </MKTypography>
                          <MKInput
                            label=""
                            multiline
                            rows={4}
                            fullWidth
                            required
                            value={form.familyExperience}
                            onChange={handleChange("familyExperience")}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <MKTypography variant="body2" fontWeight="medium" mb={1}>
                            Please name all languages you speak and explain your level{" "}
                          </MKTypography>
                          <MKInput
                            label=""
                            multiline
                            rows={4}
                            fullWidth
                            required
                            value={form.languageLevels}
                            onChange={handleChange("languageLevels")}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <MKTypography variant="body2" fontWeight="medium" mb={1}>
                            Do you have an ID or Passport? Please explain.{" "}
                          </MKTypography>
                          <MKInput
                            label=""
                            multiline
                            rows={2}
                            fullWidth
                            required
                            value={form.idStatus}
                            onChange={handleChange("idStatus")}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <MKTypography variant="body2" fontWeight="medium" mb={1}>
                            Have you completed registration on our website? Explain.{" "}
                          </MKTypography>
                          <MKInput
                            label=""
                            multiline
                            rows={2}
                            fullWidth
                            required
                            value={form.websiteRegistration}
                            onChange={handleChange("websiteRegistration")}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <MKTypography variant="body2" fontWeight="medium" mb={1}>
                            Provide your contact number and two emergency contact numbers
                          </MKTypography>
                          <MKInput
                            label=""
                            multiline
                            rows={3}
                            fullWidth
                            required
                            value={form.contacts}
                            onChange={handleChange("contacts")}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <MKTypography variant="body2" fontWeight="medium" mb={1}>
                            Current Address{" "}
                          </MKTypography>
                          <MKInput
                            label=""
                            fullWidth
                            required
                            placeholder="District, City, Country"
                            value={form.address}
                            onChange={handleChange("address")}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <MKTypography variant="body2" fontWeight="medium" mb={1}>
                            Gender
                          </MKTypography>
                          <RadioGroup row value={form.sex} onChange={handleChange("sex")} required>
                            <FormControlLabel
                              value="Male"
                              control={<Radio color="primary" />}
                              label="Male"
                            />
                            <FormControlLabel
                              value="Female"
                              control={<Radio color="primary" />}
                              label="Female"
                            />
                          </RadioGroup>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <MKTypography variant="body2" fontWeight="medium" mb={1}>
                            How did you hear about us?
                          </MKTypography>

                          <select
                            value={form.howHeard}
                            onChange={handleChange("howHeard")}
                            style={{ width: "100%", padding: "10px", borderRadius: "8px" }}
                            required
                          >
                            <option value="">Select an option</option>
                            <option value="Instagram">Instagram</option>
                            <option value="Facebook">Facebook</option>
                            <option value="Friend">Friend</option>
                            <option value="Google">Google</option>
                            <option value="Website">Website</option>
                            <option value="Other">Other</option>
                          </select>
                        </Grid>

                        {form.howHeard === "Other" && (
                          <Grid item xs={12} md={6}>
                            <MKTypography variant="body2" fontWeight="medium" mb={1}>
                              Please specify
                            </MKTypography>
                            <MKInput
                              fullWidth
                              placeholder="Tell us more..."
                              value={form.howHeardOther}
                              onChange={handleChange("howHeardOther")}
                            />
                          </Grid>
                        )}

                        {/* FIX #2: show uploaded file name below the upload box */}
                        <Grid item xs={12}>
                          <MKBox
                            p={4}
                            textAlign="center"
                            sx={{
                              border: "2px dashed #4d051d",
                              borderRadius: "1.5rem",
                              cursor: "pointer",
                            }}
                            onClick={() => document.getElementById("idUpload").click()}
                          >
                            <UploadFileIcon sx={{ fontSize: 40, color: "#740413" }} />
                            <MKTypography variant="caption" mt={1} display="block">
                              Upload any of these documents: ID, Driving License, Diplomatic ID, CV,
                              or relevant certificates (as a single merged file).
                            </MKTypography>
                            {idFile && (
                              <MKTypography
                                variant="caption"
                                mt={1}
                                display="block"
                                sx={{ color: "#740413", fontWeight: "bold" }}
                              >
                                ✓ {idFile.name}
                              </MKTypography>
                            )}
                            <input
                              type="file"
                              id="idUpload"
                              hidden
                              onChange={(e) => {
                                setIdFile(e.target.files[0]);
                                e.target.value = null;
                              }}
                            />
                          </MKBox>
                        </Grid>

                        <Grid item xs={12} display="flex" justifyContent="center">
                          {!isFormValid && (
                            <MKTypography
                              variant="caption"
                              color="error"
                              textAlign="center"
                              display="block"
                              mt={2}
                            >
                              Please complete all required fields before submitting the form.
                            </MKTypography>
                          )}
                        </Grid>

                        <Grid item xs={12} display="flex" justifyContent="center">
                          <MKButton
                            variant="gradient"
                            color="info"
                            type="submit"
                            disabled={!isFormValid || isLoading || isSubmitted}
                          >
                            {isLoading ? "Submitting..." : isSubmitted ? "Submitted" : "Apply Now"}
                          </MKButton>
                        </Grid>
                      </Grid>
                    </form>
                  </Card>

                  <Card
                    sx={{
                      p: { xs: 3, md: 5 },
                      mx: { xs: 1, md: 4 },
                      my: 4,
                      borderRadius: "2rem",
                      backgroundColor: "rgba(255,255,255,0.9)",
                      backdropFilter: "blur(25px) saturate(200%)",
                      boxShadow: "0 15px 35px rgba(0,0,0,0.08)",
                    }}
                  >
                    <MKBox display="flex" flexDirection="column" alignItems="center">
                      <MKTypography variant="h4" mb={4} fontWeight="medium" textAlign="center">
                        Other Ways to Reach Us
                      </MKTypography>

                      <Grid container spacing={3} justifyContent="center">
                        <Grid item xs={12} md={4}>
                          <MKBox
                            display="flex"
                            alignItems="center"
                            gap={2}
                            p={2}
                            borderRadius="1rem"
                            sx={{
                              backgroundColor: "rgba(245,245,245,0.6)",
                              justifyContent: "center",
                            }}
                          >
                            <PhoneIcon sx={{ fontSize: "1.5rem", color: "#007AFF" }} />
                            <MKTypography>(+250) 795 305 418</MKTypography>
                          </MKBox>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <MKBox
                            display="flex"
                            alignItems="center"
                            gap={2}
                            p={2}
                            borderRadius="1rem"
                            sx={{
                              backgroundColor: "rgba(245,245,245,0.6)",
                              justifyContent: "center",
                            }}
                          >
                            <EmailIcon sx={{ fontSize: "1.5rem", color: "#007AFF" }} />
                            <MKTypography>
                              info@helpereverywhere.com
                              <br />
                              helpereverywheremail@gmail.com
                            </MKTypography>
                          </MKBox>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <MKBox
                            display="flex"
                            alignItems="center"
                            gap={2}
                            p={2}
                            borderRadius="1rem"
                            sx={{
                              backgroundColor: "rgba(245,245,245,0.6)",
                              justifyContent: "center",
                            }}
                          >
                            <LocationOnIcon sx={{ fontSize: "1.5rem", color: "#007AFF" }} />
                            <MKTypography>KG 314 St, Kigali, Rwanda</MKTypography>
                          </MKBox>
                        </Grid>
                      </Grid>

                      <MKBox display="flex" justifyContent="center" gap={2} mt={4}>
                        <MKButton
                          variant="text"
                          color="primary"
                          iconOnly
                          component="a"
                          href="https://wa.me/250795305418"
                          target="_blank"
                        >
                          <WhatsAppIcon sx={{ fontSize: "1.5rem" }} />
                        </MKButton>
                        <MKButton
                          variant="text"
                          color="primary"
                          iconOnly
                          component="a"
                          href="https://facebook.com/helpereverywhere"
                          target="_blank"
                        >
                          <FacebookIcon sx={{ fontSize: "1.5rem" }} />
                        </MKButton>
                        <MKButton
                          variant="text"
                          color="primary"
                          iconOnly
                          component="a"
                          href="https://instagram.com/helpereverywhere"
                          target="_blank"
                        >
                          <InstagramIcon sx={{ fontSize: "1.5rem" }} />
                        </MKButton>
                      </MKBox>
                    </MKBox>
                  </Card>
                </Container>
              </MKBox>
            </Container>
          </MKBox>
        </Card>

        <MKBox pt={6} px={1} mt={6}>
          <DefaultFooter content={footerRoutes} />
        </MKBox>
        <Snackbar
          open={openModal}
          autoHideDuration={3000}
          onClose={handleCloseModal}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <Alert
            onClose={handleCloseModal}
            severity="success"
            sx={{
              backgroundColor: "#7008084d",
              color: "#fff",
              fontWeight: 600,
              borderRadius: "0.75rem",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            }}
          >
            {alertMsg}
          </Alert>
        </Snackbar>
      </>
    </>
  );
}

export default Employee;